import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useAppSelector } from 'app';
import { SmallCircularProgress } from 'common/components/loaders';
import useLocations from 'common/hooks/useLocations';
import { LocationOption } from 'common/models/location-option';
import { BroadCastType } from 'components/broadcast/models/broadcast';
import { Analytics, BroadCastSpecificAnalyticsParams, SpecificAnalytics } from 'components/broadcast/models/analytics';
import React from 'react';
import BroadCastAPI from 'components/broadcast/api/broadcast-api-v2';
import AppSnackBar from 'components/snackbar/snackbar';
import { DateRange } from '@mui/lab/DateRangePicker';
import {
  DateRangePickerDay,
  DateRangePickerDayProps,
  LocalizationProvider,
  pickersDayClasses,
  StaticDateRangePicker,
} from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import moment from 'moment';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EmailLink from 'common/components/buttons/email-link';
import axios from 'axios';
import { decideIcon } from 'common/components/icons/customerIcon';

function AnalyticsHeader({
  type,
  dateRange,
  location,
  loadingLocations,
  typeOptions,
  locationOptions,
  onDateRangeChange,
  onTypeChange,
  onLocationChange,
}: {
  type: string;
  dateRange: DateRange<Date>;
  loadingLocations: boolean;
  location?: number;
  typeOptions: any[];
  locationOptions?: LocationOption[];
  onDateRangeChange(value: DateRange<Date>): void;
  onTypeChange(value: string): void;
  onLocationChange(value: number): void;
}): React.ReactElement {
  const dealerEmail = useAppSelector((state) => state.dealerEmail);

  return (
    <Box display="flex" flexDirection="row">
      <Box flexGrow={1}>{/* <PageHeader title="Broadcasts" /> */}</Box>
      <Box ml={1}>
        <TextField
          fullWidth
          select
          size="small"
          margin="dense"
          variant="outlined"
          name="location"
          label="Location"
          value={location}
          disabled={loadingLocations}
          inputProps={{ id: 'location' }}
          onChange={(event: any): void => onLocationChange(event.target.value)}>
          {locationOptions &&
            locationOptions.map((loc) => (
              <MenuItem key={loc.dealerLocationId} value={loc.dealerLocationId}>
                {loc.name}
              </MenuItem>
            ))}
        </TextField>
      </Box>
      <Box ml={1}>
        <TextField
          fullWidth
          select
          margin="dense"
          variant="outlined"
          name="type"
          label="Type"
          value={type}
          inputProps={{ id: 'type' }}
          onChange={(event: any): void => onTypeChange(event.target.value)}>
          {typeOptions.map((typ) => (
            <MenuItem key={typ.value} value={typ.value} disabled={typ.value === 'Email' && !dealerEmail.replyToAddress}>
              {typ.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box ml={1} mt={1}>
        <DateRangeFilter value={dateRange} setValue={onDateRangeChange} />
      </Box>
    </Box>
  );
}

function DateRangeFilter({
  value,
  setValue,
}: {
  value: DateRange<Date>;
  setValue(value: DateRange<Date>): void;
}): React.ReactElement {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const renderWeekPickerDay = (date: Date, dateRangePickerDayProps: DateRangePickerDayProps<Date>) => {
    return (
      <DateRangePickerDay
        sx={{
          [`&&.${pickersDayClasses.today}`]: {
            backgroundColor: '#939afa',
            color: 'white',
            border: '#939afa',
          },
        }}
        {...dateRangePickerDayProps}
      />
    );
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const setDateRange = (startDate: Date, endDate: Date) => {
    const dateRange: DateRange<Date> = [startDate, endDate];
    setValue(dateRange);
  };

  return (
    <Box>
      <Button variant="outlined" color="primary" size="medium" onClick={handleClick}>
        Date Range
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}>
        <Grid container>
          <Grid item xs={2}>
            <Box mt={1} ml={1}>
              <ButtonGroup orientation="vertical" aria-label="vertical outlined button group">
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => {
                    const date = new Date();
                    const startDate = new Date(date.getFullYear(), date.getMonth() - 1, 1);
                    setDateRange(startDate, date);
                  }}>
                  Past Month
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => {
                    const date = new Date();
                    const startDate = new Date(date.getFullYear(), date.getMonth() - 6, 1);
                    setDateRange(startDate, date);
                  }}>
                  Past Quarter
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => {
                    const lastyear = new Date(new Date().getFullYear() - 1, 0, 1);
                    const startDate = new Date(lastyear.getFullYear(), 0, 1);
                    const endDate = new Date(lastyear.getFullYear(), 11, 31);
                    setDateRange(startDate, endDate);
                  }}>
                  Past Year
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => {
                    const date = new Date();
                    const startDate = new Date(date.getFullYear(), 0, 1);
                    setDateRange(startDate, date);
                  }}>
                  YTD
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => {
                    setValue([null, null]);
                  }}>
                  Reset
                </Button>
              </ButtonGroup>
            </Box>
          </Grid>
          <Grid item xs={10}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <StaticDateRangePicker
                displayStaticWrapperAs="desktop"
                label="date range"
                value={value}
                onChange={(newValue) => {
                  newValue[1]?.setHours(23, 59, 59);
                  setValue(newValue);
                }}
                renderDay={renderWeekPickerDay}
                renderInput={(startProps: any, endProps: any) => (
                  <>
                    <TextField {...startProps} />
                    <Box sx={{ mx: 2 }}> to </Box>
                    <TextField {...endProps} />
                  </>
                )}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </Popover>
    </Box>
  );
}

const typeOptions = [
  { value: 'Text', label: 'Text' },
  { value: 'Email', label: 'Email' },
];

function returnYtdDateRange(): DateRange<Date> {
  const date = new Date();
  const startDate = new Date(date.getFullYear(), 0, 1);
  return [startDate, date];
}

// Reusable component for clickable cells
function ClickableCell({
  value,
  onClick,
}: {
  value: number;
  onClick?: (e: React.MouseEvent) => void;
}): React.ReactElement {
  if (value > 0 && onClick) {
    return (
      <p onClick={onClick} style={{ color: '#0000EE', cursor: 'pointer', textDecoration: 'underline', margin: 0 }}>
        {value}
      </p>
    );
  }
  return <p style={{ margin: 0 }}>0</p>;
}

// Reusable table row component
function AnalyticsTableRow({
  analytics,
  type,
  onCellClick,
}: {
  analytics: Analytics;
  type: 'Email' | 'Text';
  onCellClick: (status: string) => (e: React.MouseEvent) => void;
}): React.ReactElement {
  return (
    <TableRow key={analytics.messageId}>
      <TableCell>{analytics.title}</TableCell>
      <TableCell>{moment.utc(analytics.dateSent).format('MM/DD/YYYY')}</TableCell>
      <TableCell>{analytics.segmentNames}</TableCell>
      <TableCell>
        <ClickableCell value={analytics.failed} onClick={onCellClick('failed')} />
      </TableCell>
      <TableCell>
        <ClickableCell value={analytics.success} onClick={onCellClick('success')} />
      </TableCell>
      {type === 'Email' ? (
        <>
          <TableCell>
            <ClickableCell value={analytics.opened} onClick={onCellClick('opened')} />
          </TableCell>
          <TableCell>
            <ClickableCell value={analytics.clicks} onClick={onCellClick('clicks')} />
          </TableCell>
        </>
      ) : (
        <>
          <TableCell>
            <p style={{ margin: 0 }}>{analytics.clicks}</p>
          </TableCell>
          <TableCell>
            <ClickableCell value={analytics.replies} onClick={onCellClick('replies')} />
          </TableCell>
        </>
      )}
      <TableCell>
        <ClickableCell value={analytics.unsubscribes} onClick={onCellClick('unsubscribes')} />
      </TableCell>
    </TableRow>
  );
}

function AnalyticsTab(): React.ReactElement {
  const dmsDealerIdCrm = useAppSelector((state) => state.user.dmsDealerId);
  const dmsTypeId = useAppSelector((state) => state.user.dmsTypeId);
  const defaultLocation = useAppSelector((state) => state.user.dealerId);
  const [showSnackBar] = AppSnackBar.useSnackBar();

  const [analytics, setAnaltytics] = React.useState<Analytics[]>([]);
  const [specificAnalyticsParams, setSpecificAnalyticsParams] = React.useState<BroadCastSpecificAnalyticsParams>();
  const [specificAnalytics, setSpecificAnalytics] = React.useState<SpecificAnalytics[]>([]);
  const [typeFilter, setTypeFilter] = React.useState<string>('Text');
  const [dateRangeFilter, setDateRangeFilter] = React.useState<DateRange<Date>>(returnYtdDateRange());

  // Pagination state - using 0-based index for TablePagination
  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(10);
  const [totalRecords, setTotalRecords] = React.useState<number>(0);

  const [loading, setLoading] = React.useState<boolean>(false);
  const { loading: loadingLocations, locationOptions, defaultLocationOption } = useLocations(dmsDealerIdCrm);

  const [locationFilter, setLocationFilter] = React.useState<number>(defaultLocation);
  
  // Modal state
  const [openModal, setOpenModal] = React.useState<boolean>(false);
  const [infoLoading, setInfoLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    setLocationFilter(defaultLocation);
  }, [defaultLocationOption, defaultLocation]);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setPage(0);
  }, [typeFilter, dateRangeFilter, locationFilter]);

  const fetchBroadCastsAnalytics = React.useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await BroadCastAPI.getBroadCastsAnalytics(
        dmsDealerIdCrm,
        typeFilter,
        dateRangeFilter,
        locationFilter,
        page + 1, // API uses 1-based page numbers
        rowsPerPage,
      );
      if (response && response.length > 0) {
        setTotalRecords(response[0].totalRecords);
      } else {
        setTotalRecords(0);
      }
      setAnaltytics(response);
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.log('Request cancelled:', error.message);
        return;
      }

      showSnackBar({ type: 'error', message: 'Unable to fetch broadcasts analytics' });
    } finally {
      setLoading(false);
    }
  }, [dmsDealerIdCrm, typeFilter, dateRangeFilter, locationFilter, page, rowsPerPage, showSnackBar]);

  React.useEffect(() => {
    fetchBroadCastsAnalytics();
  }, [fetchBroadCastsAnalytics]);

  const handleModalClose = React.useCallback((): void => {
    setOpenModal(false);
  }, []);

  const handleSnippetClick = React.useCallback(
    (analytics: Analytics, type: string, status: string) => async (event: React.MouseEvent): Promise<void> => {
      setSpecificAnalyticsParams({
        title: analytics.title,
        dateSent: analytics.dateSent,
        customerGroup: analytics.segmentNames,
      });
      setOpenModal(true);
      setInfoLoading(true);
      try {
        const specificAnalyticsData = await BroadCastAPI.getBroadcastSpecificAnalytics(
          analytics.messageId,
          type,
          status,
          analytics.usageHistoryId,
        );
        setSpecificAnalytics(specificAnalyticsData);
      } catch (error) {
        showSnackBar({ type: 'error', message: 'Unable to fetch specific analytics' });
      } finally {
        setInfoLoading(false);
      }
    },
    [showSnackBar],
  );

  const FILE_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  const FILE_EXTENSION = '.xlsx';
  
  const formatAddress = (data: SpecificAnalytics): string => {
  const parts = [
    data.address1,
    data.address2,
    data.city,
    data.stateProvince,
    data.zipCode,
  ].map((part) => part?.trim()).filter(Boolean);
  return parts.join(', ');
};

  const exportToCSV = React.useCallback(
    (csvData: SpecificAnalytics[], fileName: string) => {
      if (!csvData || csvData.length === 0) {
        showSnackBar({ type: 'warning', message: 'No data to export' });
        return;
      }

      // Define headers based on dmsTypeId
      const headers =
        dmsTypeId === 1
          ? [
              'Broadcast Title',
              'Date Sent',
              'Customer Group',
              'Organization',
              'Name',
              'Email',
              'Cellphone',
              'Billing Address',
              'Notes',
              'Error',
              'Reply',
            ]
          : [
              'Broadcast Title',
              'Date Sent',
              'Customer Group',
              'Organization',
              'Name',
              'Email',
              'Cellphone',
              'Billing Address',
              'Notes',
              'Error',
              'Reply',
            ];

      const exportData = csvData.map((data) => {
        const baseData = {
          'Broadcast Title': specificAnalyticsParams?.title ?? '',
          'Date Sent': specificAnalyticsParams ? moment.utc(specificAnalyticsParams.dateSent).format('MM/DD/YYYY') : '',
          'Customer Group': specificAnalyticsParams?.customerGroup ?? '',
          'Organization': data.isOrganization && dmsTypeId !== 1 ? data.customerName : '',
        };

        const nameData = { 'Name':  data.contactPrimaryId ? data.contactName : data.customerName }

        const contactData = {
          Email: data.email ?? '',
          Cellphone: data.cellphone ?? '',
          'Billing Address': formatAddress(data),
          Notes: data.notes ?? '',
          Error: data.error ?? '',
          Reply: data.reply ?? '',
        };

        return { ...baseData, ...nameData, ...contactData };
      });

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Calculate column widths based on content
      const colWidths = headers.map((header, index) => {
        const columnKey = header;
        const maxContentLength = Math.max(
          header.length,
          ...exportData.map((row) => {
            const value = row[columnKey as keyof typeof row];
            return value ? String(value).length : 0;
          }),
        );
        // Add padding and set minimum/maximum widths
        return { wch: Math.min(Math.max(maxContentLength + 2, 10), 50) };
      });

      ws['!cols'] = colWidths;

      // Apply bold style to header row
      const headerRange = XLSX.utils.decode_range(ws['!ref'] || 'A1');
      for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (ws[cellAddress]) {
          ws[cellAddress].s = {
            font: { bold: true },
            fill: { fgColor: { rgb: 'E0E0E0' } },
            alignment: { horizontal: 'center' },
          };
        }
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'data');

      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: FILE_TYPE });
      FileSaver.saveAs(blob, `${fileName}${FILE_EXTENSION}`);
    },
    [specificAnalyticsParams, dmsTypeId, showSnackBar],
  );

  const handleErrorMessage = React.useCallback((errorMessage: string): string => {
    try {
      const parsedMessage = JSON.parse(errorMessage);
      const message =
        parsedMessage.Exception?.FailureDetails?.ErrorMessage &&
        parsedMessage.Exception.FailureDetails.ErrorMessage.trim() !== ''
          ? parsedMessage.Exception.FailureDetails.ErrorMessage
          : parsedMessage.ErrorMessage;
      return message;
    } catch (error) {
      return errorMessage;
    }
  }, []);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const tableHeaders =
    typeFilter === 'Email'
      ? ['Title', 'Date Sent', 'Customer Groups', 'Bounces', 'Success', 'Opened', 'Clicks', 'Unsubscribes']
      : ['Title', 'Date Sent', 'Customer Groups', 'Fails', 'Success', 'Clicks', 'Replies', 'Unsubscribes'];

  return (
    <Paper square style={{ padding: '10px' }}>
      <AnalyticsHeader
        dateRange={dateRangeFilter}
        type={typeFilter}
        loadingLocations={loadingLocations}
        location={locationFilter}
        locationOptions={locationOptions}
        typeOptions={typeOptions}
        onTypeChange={(type: BroadCastType): void => setTypeFilter(type)}
        onDateRangeChange={(dateRange: DateRange<Date>): void => setDateRangeFilter(dateRange)}
        onLocationChange={(location: number): void => setLocationFilter(location)}
      />
      <Box mt={2} component="section">
        {loading ? (
          <Box height="500px" margin="auto" display="flex" justifyContent="center" alignItems="center">
            <SmallCircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer style={{ maxHeight: '500px' }}>
              <Table stickyHeader id="analytics-table" aria-label="Analytics table">
                <TableHead>
                  <TableRow>
                    {tableHeaders.map((header) => (
                      <TableCell key={header}>{header}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analytics?.map((analyticsItem: Analytics) => (
                    <AnalyticsTableRow
                      key={analyticsItem.messageId}
                      analytics={analyticsItem}
                      type={typeFilter as 'Email' | 'Text'}
                      onCellClick={(status: string) =>
                        handleSnippetClick(analyticsItem, typeFilter.toLowerCase(), status)
                      }
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
        <TablePagination
              rowsPerPageOptions={[10, 20, 30]}
              component="div"
              count={totalRecords}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
      </Box>
      
      {/* Modal for Customer List */}
      <Dialog
        open={openModal}
        onClose={handleModalClose}
        maxWidth="xl"
        style={{margin: '5%'}}
        fullWidth
        aria-labelledby="customer-list-dialog">
        <DialogTitle style={{padding:'2px 24px'}}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">Customer List</Typography>
            <Box>
              <Tooltip title="Export Table">
                <IconButton
                  onClick={() => {
                    exportToCSV(specificAnalytics, specificAnalyticsParams?.title ?? 'analytics');
                  }}>
                  <FileDownloadIcon />
                </IconButton>
              </Tooltip>
              <IconButton onClick={handleModalClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {infoLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
              <CircularProgress size={48} />
            </Box>
          ) : (
            <TableContainer style={{ maxHeight: '500px' }}>
              <Table stickyHeader id="specific-analytics-table" aria-label="Specific Analytics table">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Cell Phone</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Billing Address</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell>Error</TableCell>
                    <TableCell>Reply</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {specificAnalytics?.map((analyticsItem: SpecificAnalytics) => (
                    <TableRow key={analyticsItem.crmCustomerId}>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          {/* Primary: Contact or Customer */}
                          <Box display="flex" alignItems="center" mr={1}>
                            {decideIcon(
                              !!analyticsItem.contactPrimaryId,
                              dmsTypeId !== 1 && analyticsItem.isOrganization,
                              dmsTypeId
                            )}
                            <Box ml={1}>
                              {analyticsItem.contactPrimaryId
                                ? analyticsItem.contactName
                                : analyticsItem.customerName}
                            </Box>
                          </Box>
                          {/* Secondary: Show customer/organization if contact exists */}
                          {analyticsItem.contactPrimaryId && (
                            <>
                              <Box mx={0.5} fontWeight="bold">|</Box>
                              <Box display="flex" alignItems="center">
                                {decideIcon(
                                  false,
                                  dmsTypeId !== 1 && !!analyticsItem.isOrganization,
                                  dmsTypeId
                                )}
                                <Box ml={0.5}>{analyticsItem.customerName}</Box>
                              </Box>
                            </>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{analyticsItem.cellphone ?? ''}</TableCell>
                      <TableCell>
                        {analyticsItem.email ? <EmailLink email={analyticsItem.email} /> : null}
                      </TableCell>
                      <TableCell>{formatAddress(analyticsItem)}</TableCell>
                      <TableCell>{analyticsItem.notes}</TableCell>
                      <TableCell>{handleErrorMessage(analyticsItem.error)}</TableCell>
                      <TableCell>{analyticsItem.reply}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
    </Paper>
  );
}

export default AnalyticsTab;
